type Identifiable = { id: string };

/**
 * Merges organic, early access and promoted events into fixed, collision-free slots: the first N early access
 * ranks carry the N early access cards, ads keep their ranks, and every other position falls through to organic.
 *
 * Callers must dedupe first (an event must appear in at most one of the three lists).
 */
export function mergeFeedWithEarlyAccess<
    Organic extends Identifiable,
    EarlyAccess extends Identifiable,
    Promoted extends Identifiable,
>({
    organicEvents,
    earlyAccessEvents,
    promotedEvents,
    earlyAccessRanks,
    promotedRanks,
}: {
    organicEvents: Organic[];
    earlyAccessEvents: EarlyAccess[];
    promotedEvents: Promoted[];
    earlyAccessRanks: number[];
    promotedRanks: number[];
}): (Organic | EarlyAccess | Promoted)[] {
    const activeEarlyAccessRanks = earlyAccessRanks.slice(0, earlyAccessEvents.length);
    const total = organicEvents.length + earlyAccessEvents.length + promotedEvents.length;
    const merged: (Organic | EarlyAccess | Promoted)[] = [];
    let organicIndex = 0;
    let earlyAccessIndex = 0;
    let promotedIndex = 0;

    for (let position = 1; merged.length < total; position += 1) {
        if (
            activeEarlyAccessRanks.includes(position) &&
            earlyAccessIndex < earlyAccessEvents.length
        ) {
            merged.push(earlyAccessEvents[earlyAccessIndex]);
            earlyAccessIndex += 1;
        } else if (promotedRanks.includes(position) && promotedIndex < promotedEvents.length) {
            merged.push(promotedEvents[promotedIndex]);
            promotedIndex += 1;
        } else if (organicIndex < organicEvents.length) {
            merged.push(organicEvents[organicIndex]);
            organicIndex += 1;
        } else if (earlyAccessIndex < earlyAccessEvents.length) {
            merged.push(earlyAccessEvents[earlyAccessIndex]);
            earlyAccessIndex += 1;
        } else if (promotedIndex < promotedEvents.length) {
            merged.push(promotedEvents[promotedIndex]);
            promotedIndex += 1;
        }
    }

    return merged;
}
